<template>
	<Modals
		:title="`${title} file`"
		:display="display"
		@show="closeFileUploadModal"
		:parentTag="'form'"
		:novalidate="'novalidate'"
		:fullMobile="true"
	>
		<template #modal-content>
			<div class="upload">
				<div
					:class="['upload__browse', droppingFile && 'dropping-file']"
					@drop.prevent="addDroppedFile"
					@dragover.prevent
					@dragover="fileDragIn"
					@dragleave="fileDragOut"
				>
					<div class="upload__browse-title">
						Drag and Drop Files Here <br />
						or
					</div>

					<input
						ref="fileUpload"
						type="file"
						accept="application/pdf"
						@change="getFileUpload"
					/>

					<div class="upload__file-requirement">
						<div v-if="isImageFile">
							Maximum upload file size: {{ showMaximunSize }}
							<br />
							Ideal Dimensions: {{ showWidthDimensions }} by
							{{ showHeightDimensions }}
						</div>
						File types accepted: {{ showAcceptedFileType }} <br />
					</div>
				</div>
				<!-- DEV NOTE: Notification Block will be created in Ticket 25725 -->
				<div class="upload__file-info">
					<div class="upload__file">
						<div
							class="upload__file-name"
							v-if="showfileNameUploader"
						>
							<i class="icon icon-document"></i>
							<a class="link" tabindex="0" @click="showFile">{{
								showfileNameUploader
							}}</a>
							<Button
								className="btn--icon-only"
								iconClassName="icon-delete-glenn"
								:btnClick="clearFileUpload"
								:iconBeforeLabel="true"
							/>
						</div>
						<!-- DEV NOTE: Only displays when the user hasn't uploaded a file -->
						<div class="upload__no-file" v-else>
							No File Selected
						</div>
					</div>
					<div class="upload__comment">
						<!-- DEV NOTE: Component is pending development's implementation. No help text. No character count, unless a character limit is needed database -->
						<Textarea
							:className="'textarea__input'"
							:placeholder="changeComment"
							:rows="5"
							:cols="30"
							:maxLength="200"
							:required="true"
							label="Comment"
							v-model="comment"
						/>
					</div>
				</div>
			</div>
		</template>
		<template #modal-footer>
			<ButtonGroup
				:items="[
					{
						id: uid,
						className: 'btn--primary',
						labelText: title,
						type: 'submit',
						disabled: !hasCommentFile,
						btnClick: submitFile
					},
					{
						id: 'cancelButtonModal',
						className: 'btn--link',
						labelText: 'Cancel',
						btnClick: cancelAction
					}
				]"
			/>
		</template>
	</Modals>
	<!---------- DEV NOTES -----------------
    -- Use the fullscreen variant of the modal
	-- Image Requirements:
    -- Need to specify the maximum upload file size
    -- Need to specify the ideal dimensions of file for specific logo. Confluence doc references specific dimensions for Main and Report logo.
	-->
</template>

<script>
import Textarea from '@/components/forms/textarea/textarea.vue';
import Button from '@/components/forms/button/button';
import Modals from '@/components/shells/modal/modals';
import ButtonGroup from '@/components/forms/button/button-group';
import { mapActions } from 'vuex';
import _ from 'lodash';

export default {
	name: 'FileUpload',
	components: {
		Textarea,
		Button,
		Modals,
		ButtonGroup
	},
	model: {
		prop: 'display',
		event: 'show'
	},
	props: {
		title: {
			type: String,
			default: ''
		},
		display: {
			type: Boolean,
			default: false
		},
		validateBeforeClose: {
			type: Boolean,
			default: false
		},
		formId: {
			type: Number
		},
		maximunSize: {
			type: Number,
			default: 0
		},
		acceptedFileType: {
			type: String,
			default: 'all'
		},
		maxDimensions: {
			type: Object,
			default: () => {}
		}
	},
	data() {
		return {
			file: null,
			comment: '',
			droppingFile: false
		};
	},
	computed: {
		showfileNameUploader() {
			return this.file ? this.file.name : '';
		},

		changeComment() {
			let typeFile = 'file';
			if (this.acceptedFileType == 'pdf') typeFile = 'pdf';
			else if (this.acceptedFileType === 'all') typeFile = 'file';
			else if (this.isImageFile) {
				typeFile = 'logo';
			}

			return 'Please provide a reason for the ' + typeFile + ' change';
		},
		isImageFile() {
			const imageExtension = ['jpg', 'jpeg', 'png', 'tif', 'gif'];
			let fileTypesToAllow = this.acceptedFileType.split(',');

			let isImage = imageExtension.some(ext =>
				fileTypesToAllow.includes(ext)
			);

			return isImage;
		},
		showMaximunSize() {
			return this.maximunSize;
		},
		showAcceptedFileType() {
			return this.acceptedFileType;
		},
		showWidthDimensions() {
			const { width } = this.maxDimensions || {};
			if (width) {
				return this.maxDimensions.width;
			}
			return null;
		},
		showHeightDimensions() {
			const { height } = this.maxDimensions || {};
			if (height) {
				return this.maxDimensions.height;
			}
			return null;
		},
		hasCommentFile() {
			return !!this.comment && this.file != null;
		}
	},
	methods: {
		...mapActions('form', ['uploadFile']),

		closeFileUploadModal() {
			if (!this.validateBeforeClose) {
				this.cancelAction();
			}
		},
		cancelAction() {
			this.clearFileUpload();
			this.$emit('show', false);
		},
		showFile() {
			const type = 'pdf';
			var newBlob = new Blob([this.file], {
				type: `application/${type}`
			});
			const fileUrl = URL.createObjectURL(newBlob);

			window.open(fileUrl, '_blank');
		},
		getFileUpload() {
			this.file = this.$refs.fileUpload.files[0];
		},
		addDroppedFile(e) {
			e.preventDefault();
			let droppedFiles = e.dataTransfer.files;
			if (!droppedFiles) return;
			this.file = droppedFiles[0];
			this.droppingFile = false;
		},
		clearFileUpload() {
			this.file = null;
			this.comment = null;
			this.$refs.fileUpload.value = '';
		},

		updateToast(val) {
			this.$store.dispatch('toastMessages/show', {
				toastId: 'upload.action',
				statusCode: val.status,
				message: val.message,
				slot: 'default',
				useIcon: true,
				userDismissable: true
			});
		},
		getFileExtension() {
			const self = this;
			let fileName = self.file.name;
			let fileExtension = fileName.split('.').pop();
			return fileExtension;
		},
		isFileAllowed(fileExtension) {
			let isFileAllowed =
				this.acceptedFileType === 'all'
					? true
					: this.acceptedFileType.split(',').includes(fileExtension);
			return isFileAllowed;
		},
		async submitFile() {
			let formData = new FormData();
			let fileExtension = this.getFileExtension();

			formData.append('PdfFile', this.file);
			formData.append('FormId', this.formId || 0);
			formData.append('Message', this.comment);

			if (!this.isFileAllowed(fileExtension)) {
				this.updateToast({
					status: 400,
					message: 'File type is not allowed'
				});
			} else {
				await this.uploadFile(formData).then(response => {
					if (!_.isEmpty(response)) {
						this.$emit('change', {
							laserFormFileName: response.laserFormFileName,
							pdfData: response.pdfData,
							minutesAgo: 0
						});
						this.cancelAction();
					}
				});
			}
		},
		fileDragIn() {
			this.droppingFile = true;
		},
		fileDragOut() {
			this.droppingFile = false;
		}
	}
};
</script>

<style lang="scss">
/**
    * [Description]
    *
    * Modal content for File Upload
    * Utilized in Logo Manager
    *
    * Can use SCSS variables = TRUE
    *
    * [Table of Contents]
    *
    * CONTENT LAYOUT
 */

/*------------------------------------*\
  #CONTENT LAYOUT
\*------------------------------------*/

.upload {
	display: flex;
	flex-direction: column;
	line-height: 1.5;

	@include mq($from: lg) {
		flex-direction: row;
	}

	&__browse {
		text-align: center;

		@include mq($until: lg) {
			margin-bottom: $gutter-xxl;
		}

		@include mq($from: md-l) {
			background: $neutral-4;
			border: 1px dashed $neutral-3;
			padding: $padding-xl;
			margin-right: $gutter-lg;
			border-radius: $modal-border-radius;
		}

		@include mq($from: lg) {
			flex-grow: 1;
		}
	}

	&__browse-title {
		display: none;

		@include mq($from: md-l) {
			display: block;
			font-size: $default-font-size;
			font-weight: $medium-font-weight;
			line-height: $line-height-ratio + 0.5;
		}
	}

	&__file-requirement {
		margin-top: $gutter-xl;
		font-size: $default-font-size - 4;
		line-height: $line-height-ratio;
		color: $neutral-1;
	}

	&__file-info {
		@include mq($from: lg) {
			flex-grow: 2;
		}
	}

	&__file {
		margin-bottom: $gutter-xl;

		@include mq($from: lg) {
			margin-top: $gutter-md;
		}
	}

	&__file-name {
		display: flex;
		align-items: center;
	}

	&__no-file {
		font-size: $default-font-size - 4;
	}
}

.upload__file-name {
	.btn--icon-only {
		margin-left: auto;
	}

	.icon-document {
		font-size: $icon-font-size - 4;
	}

	.link {
		margin-left: $gutter-sm;
	}
}

.dropping-file {
	background-color: $focus-color;
}
</style>

<style lang="scss" scoped>
.modal {
	@include mq($from: md) {
		width: 90%;
	}
	@include mq($from: lg) {
		width: 80%;
	}
}
</style>
